import React from 'react';
import { PrismicRichText } from '@prismicio/react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import NewsletterFormAlt from '@components/shared/NewsletterFormAlt';
import { BodyLG, BodySM } from '@components/shared/TextTypes';
import TwoColumn from '@components/shared/TwoColumn';
import { BREAKPOINT, DEFAULT_KLAVIYO_LIST_ID, MEDIA } from '@constants';
import { PageSlice, SliceNewsletterDetails } from '@contracts';
import { TEXT_STYLES, TextVariant } from './design/Typography';
import { Gradient2Style } from './SharedStyles';

interface Props {
  slice: PageSlice;
}

const NewsletterCustom = ({ slice }: Props) => {
  const sliceDetails = slice.primary as SliceNewsletterDetails;
  const { newsletter_button_text, consent_text, newsletter_id, image, newsletter_background, headline, body_text } = sliceDetails;
  const { t } = useTranslation();

  const getListId = (): string => {
    if (newsletter_id) {
      return typeof newsletter_id === 'string' ? newsletter_id : newsletter_id.text || '';
    }
    return DEFAULT_KLAVIYO_LIST_ID;
  };

  const getBodyText = (): string => {
    if (body_text) {
      return typeof body_text === 'string' ? body_text : body_text.text || '';
    }
    return '';
  };

  const getBackground = (): string => {
    if (newsletter_background) {
      return typeof newsletter_background === 'string' ? newsletter_background : newsletter_background.text || '';
    }
    return Colors.ecru50;
  };

  const getButtonText = (): string => {
    if (newsletter_button_text) {
      return typeof newsletter_button_text === 'string' ? newsletter_button_text : newsletter_button_text.text || '';
    }
    return t('components.send');
  };

  const hasImage = Boolean(image?.url);

  return (
    <Container $background={getBackground()} className={!hasImage ? 'no-image' : ''}>
      {hasImage ? (
        <Inner>
          <TwoColumn gap="60px" breakpoint="1024px" alignment="center">
            <TextColumnWrap $hasImage={hasImage}>
              <TextColumnInner>
                <TextWrap $hasImage={hasImage}>
                  {headline?.richText && (
                    <Title>
                      <PrismicRichText field={headline.richText} />
                    </Title>
                  )}
                  {getBodyText() && <StyledBodyLG>{getBodyText()}</StyledBodyLG>}
                </TextWrap>
                <NewsletterFormAlt listId={getListId()} textColor={Colors.navyBlue} buttonText={getButtonText()} />
                {consent_text && <ConsentText>{typeof consent_text === 'string' ? consent_text : consent_text.text}</ConsentText>}
              </TextColumnInner>
            </TextColumnWrap>
            {image?.url && (
              <ImageColumnWrap>
                <StyledImg src={image.url} alt={image.alt || 'Newsletter image'} />
              </ImageColumnWrap>
            )}
          </TwoColumn>
        </Inner>
      ) : (
        <NoImageInner>
          <TwoColumn gap="60px" breakpoint="1024px" alignment="center">
            <NoImageTextColumnWrap>
              {headline?.richText && (
                <Title>
                  <PrismicRichText field={headline.richText} />
                </Title>
              )}
              {getBodyText() && <NoImageStyledBodyLG>{getBodyText()}</NoImageStyledBodyLG>}
            </NoImageTextColumnWrap>
            <NoImageEmailColumnWrap>
              <NewsletterFormAlt listId={getListId()} textColor={Colors.navyBlue} buttonText={getButtonText()} />
              {consent_text && <ConsentText>{typeof consent_text === 'string' ? consent_text : consent_text.text}</ConsentText>}
            </NoImageEmailColumnWrap>
          </TwoColumn>
        </NoImageInner>
      )}
    </Container>
  );
};

export default NewsletterCustom;

const Container = styled.div<{ $background?: string }>`
  background: ${(p) => p.$background || Colors.ecru50};
  padding: 0;

  ${MEDIA.laptop} {
    padding: 32px 0 0;
  }

  /* When no image, add padding like NewsletterSubscribe */
  &.no-image {
    padding: 60px;

    @media (max-width: 1024px) {
      padding: 30px;
    }
  }

  & .newsletter-form-alt {
    & input {
      margin-bottom: 0;
    }
    & p {
      display: none;
    }

    & .input-container {
      gap: 10px;

      & button {
        background: ${Colors.lilac};
        color: ${Colors.cream};
      }

      ${MEDIA.laptop} {
        flex-direction: column;
      }
    }
  }
`;

const Inner = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const TextColumnWrap = styled.div<{ $hasImage: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 0 0 30px;
  gap: 24px;
  width: ${(props) => (props.$hasImage ? '50%' : '100%')};
  color: ${Colors.navyBlue};

  ${MEDIA.laptop} {
    align-items: center;
    width: 100%;
    padding: 0 15px 32px;
  }
`;

const TextColumnInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 490px;
  width: 100%;
  gap: 24px;
`;

const TextWrap = styled.div<{ $hasImage: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  text-align: center;
`;

const ImageColumnWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  width: 50%;
  height: 100%;

  ${MEDIA.laptop} {
    width: 100%;
  }
`;

const NoImageTextColumnWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 20px;
  max-width: 420px;
  color: ${Colors.navyBlue};

  @media (max-width: 1024px) {
    align-items: center;
  }
`;

const NoImageEmailColumnWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 20px;
  max-width: 420px;
  color: ${Colors.navyBlue};

  @media (max-width: 1024px) {
    align-items: center;
    padding-top: 40px;
  }
`;

const NoImageInner = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const NoImageStyledBodyLG = styled(BodyLG)`
  text-align: left;
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;

  @media (max-width: 1024px) {
    text-align: center;
    margin-bottom: 0px;
  }
`;

const Title = styled.div`
  & > * {
    ${TEXT_STYLES[TextVariant.Heading3]};
    color: ${Colors.navyBlue};

    @media (max-width: ${BREAKPOINT.tablet}px) {
      ${TEXT_STYLES[TextVariant.Heading4]};
      text-align: center;
    }

    @media (max-width: ${BREAKPOINT.mobileM}px) {
      ${TEXT_STYLES[TextVariant.Heading5]};
    }

    & span.italic {
      font-style: italic;
    }

    & span.gradient2 {
      ${Gradient2Style}
    }
  }
`;

const StyledBodyLG = styled(BodyLG)`
  text-align: center;
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;

  ${MEDIA.laptop} {
    text-align: center;
    margin-bottom: 0px;
  }
`;

const ConsentText = styled(BodySM)`
  color: #001d3b;
  opacity: 0.5;
  text-align: left;
`;

const StyledImg = styled.img`
  width: 100%;
  height: auto;
  max-height: 640px;
  object-fit: cover;
  border-radius: 4px;

  ${MEDIA.laptop} {
    max-height: 100%;
  }
}
`;
